import { useMutation, useQueryClient } from '@tanstack/react-query'
import { gql } from 'graphql-request'
import { gqlClient } from '@/lib/graphql'

const TRANSLATE_CHAPTER = gql`
  mutation TranslateChapter($chapterId: ID!) {
    translateChapter(input: { chapterId: $chapterId }) {
      errors
    }
  }
`

const UPDATE_SEGMENT = gql`
  mutation UpdateSegment($segmentId: ID!, $targetText: String) {
    updateSegment(input: { segmentId: $segmentId, targetText: $targetText }) {
      segment { id targetText status translated }
      errors
    }
  }
`

export function useTranslateChapter() {
  return useMutation({
    mutationFn: async (chapterId: string) => {
      const res = await gqlClient.request<{ translateChapter: { errors: string[] } }>(
        TRANSLATE_CHAPTER,
        { chapterId },
      )
      if (res.translateChapter.errors.length) throw new Error(res.translateChapter.errors.join(', '))
    },
  })
}

const SYNTHESIZE_AUDIO = gql`
  mutation SynthesizeAudio($segmentId: ID!) {
    synthesizeAudio(input: { segmentId: $segmentId }) {
      segment { id audioStatus audioUrl }
      errors
    }
  }
`

// Enqueues narration synthesis for a segment. The job is async, so the new
// audio arrives via the chapter query polling (useChapter awaitingAudio), not
// in this response.
export function useSynthesizeAudio(chapterId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (segmentId: string) => {
      const res = await gqlClient.request<{
        synthesizeAudio: { segment: { id: string } | null; errors: string[] }
      }>(SYNTHESIZE_AUDIO, { segmentId })
      if (res.synthesizeAudio.errors.length) throw new Error(res.synthesizeAudio.errors.join(', '))
      return res.synthesizeAudio.segment
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['chapter', chapterId] }),
  })
}

export type AudioRegenChoice = 'now' | 'on_play' | 'skip'

const SET_AUDIO_REGEN_POLICY = gql`
  mutation SetAudioRegenPolicy($segmentId: ID!, $policy: String!) {
    setAudioRegenPolicy(input: { segmentId: $segmentId, policy: $policy }) {
      segment { id audioStatus audioRegenPolicy needsAudioDecision regenerateBeforePlay }
      errors
    }
  }
`

// Records what to do about narration that's gone stale after a wording edit:
// "now" enqueues synthesis, "on_play"/"skip" persist the choice. Re-fetches the
// chapter so the indicator and play behavior update.
export function useSetAudioRegenPolicy(chapterId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ segmentId, policy }: { segmentId: string; policy: AudioRegenChoice }) => {
      const res = await gqlClient.request<{
        setAudioRegenPolicy: { segment: { id: string } | null; errors: string[] }
      }>(SET_AUDIO_REGEN_POLICY, { segmentId, policy })
      if (res.setAudioRegenPolicy.errors.length) throw new Error(res.setAudioRegenPolicy.errors.join(', '))
      return res.setAudioRegenPolicy.segment
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['chapter', chapterId] }),
  })
}

const SUGGEST_WORDING = gql`
  mutation SuggestWording(
    $segmentId: ID!
    $selectedText: String!
    $precedingText: String
    $followingText: String
  ) {
    suggestWording(
      input: {
        segmentId: $segmentId
        selectedText: $selectedText
        precedingText: $precedingText
        followingText: $followingText
      }
    ) {
      suggestions
      errors
    }
  }
`

interface SuggestWordingVars {
  segmentId: string
  selectedText: string
  // Text around the selection in the same paragraph, so the AI replaces only
  // the selected words and keeps the surrounding text fixed.
  precedingText: string
  followingText: string
}

// Asks the AI for up to 3 alternative phrasings of a selected phrase. Stateless
// (no persistence) — applying a choice goes through useUpdateSegment.
export function useSuggestWording() {
  return useMutation({
    mutationFn: async (vars: SuggestWordingVars) => {
      const res = await gqlClient.request<{
        suggestWording: { suggestions: string[]; errors: string[] }
      }>(SUGGEST_WORDING, vars)
      if (res.suggestWording.errors.length) throw new Error(res.suggestWording.errors.join(', '))
      return res.suggestWording.suggestions
    },
  })
}

export function useUpdateSegment(chapterId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ segmentId, targetText }: { segmentId: string; targetText: string }) => {
      const res = await gqlClient.request<{
        updateSegment: { segment: { id: string; targetText: string; status: string; translated: boolean } | null; errors: string[] }
      }>(UPDATE_SEGMENT, { segmentId, targetText })
      if (res.updateSegment.errors.length) throw new Error(res.updateSegment.errors.join(', '))
      return res.updateSegment.segment!
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['chapter', chapterId] }),
  })
}
